import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { problemMessage } from "@/lib/birds";
import type { components, paths } from "@breenova/api-client";

type Schemas = components["schemas"];

export type LocationNode = Schemas["GetLocationsLocationNode"];
export type LocationKind = Schemas["LocationKind"];

type JsonBody<P, M extends keyof P> = P[M] extends {
  requestBody: { content: { "application/json": infer B } };
}
  ? B
  : never;

export type CreateLocationBody = JsonBody<paths["/api/v1/locations"], "post">;
export type UpdateLocationBody = JsonBody<paths["/api/v1/locations/{id}"], "put">;

/**
 * Everything the location screens read is one list, so everything they write invalidates
 * that one key. `birds` goes with it: a cage's bird count is part of the node, and a
 * bird's screen prints the cage's path.
 */
export const locationKeys = {
  all: ["locations"] as const,
  tree: (includeInactive: boolean) => ["locations", { includeInactive }] as const,
};

/**
 * The whole tree, inactive rows included when asked.
 *
 * Not `OnlyCages` — the management screen is the one place the aviaries and sections are
 * the subject rather than scaffolding for a cage picker.
 */
export function useLocationTree(includeInactive = true) {
  return useQuery({
    queryKey: locationKeys.tree(includeInactive),
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/locations", {
        params: { query: { IncludeInactive: includeInactive } },
      });

      if (error || !data) {
        throw new Error("Could not load your aviary.");
      }

      return data.locations;
    },
  });
}

function useLocationMutationSuccess() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: locationKeys.all });

    // A bird's screen prints its cage's path, and the bird list filters by cage. Both
    // are wrong the moment a cage is renamed or removed.
    void queryClient.invalidateQueries({ queryKey: ["birds"] });
  };
}

export function useCreateLocation() {
  const onSuccess = useLocationMutationSuccess();

  return useMutation({
    mutationFn: async (body: CreateLocationBody) => {
      const { data, error } = await api.POST("/api/v1/locations", { body });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not add that location."));
      }

      return data;
    },
    onSuccess,
  });
}

export function useUpdateLocation() {
  const onSuccess = useLocationMutationSuccess();

  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateLocationBody }) => {
      const { data, error } = await api.PUT("/api/v1/locations/{id}", {
        params: { path: { id } },
        body,
      });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not save that location."));
      }

      return data;
    },
    onSuccess,
  });
}

/**
 * The server refuses this for a location that still holds birds or still has locations
 * inside it, and says which. Those refusals are the useful part — they arrive as the
 * problem detail and go straight on screen.
 */
export function useDeleteLocation() {
  const onSuccess = useLocationMutationSuccess();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.DELETE("/api/v1/locations/{id}", {
        params: { path: { id } },
      });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not remove that location."));
      }

      return data;
    },
    onSuccess,
  });
}

export interface LocationTreeNode extends LocationNode {
  children: LocationTreeNode[];
}

/**
 * The flat list rebuilt into the tree it describes.
 *
 * The API flattens deliberately — a `depth` and a readable `path` are all a picker needs,
 * and a recursive DTO is a shape .NET's OpenAPI generator emits badly. But a screen whose
 * subject IS the structure has to draw the structure, so it is rebuilt here rather than
 * asked for in a second shape.
 *
 * A node whose parent is missing from the list is attached at the root instead of being
 * dropped. That happens when the parent is inactive and the caller asked for active rows
 * only — and silently hiding a breeder's cages because the row above them was retired is
 * how a management screen loses their trust.
 */
export function buildLocationTree(nodes: readonly LocationNode[]): LocationTreeNode[] {
  const byId = new Map<string, LocationTreeNode>(
    nodes.map((node) => [node.id, { ...node, children: [] }]),
  );

  const roots: LocationTreeNode[] = [];

  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;

    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sort = (list: LocationTreeNode[]): LocationTreeNode[] => {
    // By code, the way it is painted on the door: a breeder scanning for B-12 reads down
    // the codes, and `B-2` before `B-12` is what a plain string sort would give them.
    list.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    list.forEach((child) => sort(child.children));

    return list;
  };

  return sort(roots);
}

/** Live birds anywhere at or below this node — what a section's or aviary's row shows. */
export function countBirdsBelow(node: LocationTreeNode): number {
  return node.children.reduce((total, child) => total + countBirdsBelow(child), node.birdCount);
}

/** Cages anywhere at or below this node. An aviary's own `birdCount` is always zero. */
export function countCagesBelow(node: LocationTreeNode): number {
  return node.children.reduce(
    (total, child) => total + countCagesBelow(child),
    node.kind === "Cage" ? 1 : 0,
  );
}
